import { useState, useEffect, useCallback } from "react";
import { Button, Icon, Chip } from "@legal-500/legal500-design-system";
import { SearchableSelect } from "../Form/SearchableSelect";
import type { Entity } from "../EntityPicker";
import type { FieldArrayWithId, FieldErrors, FieldValues } from "react-hook-form";
import { contacts as contactsService } from "../../api/contacts.service";
import { offices as officesService } from "../../api/offices.service";
import styles from "./EliteMetadataConfiguration.module.css";

export type OfficeContactPair = {
  office_id: number;
  contact_ids: number[];
};

// Form mode props (when used with useFieldArray)
type FormModeProps = {
  /** Fields from useFieldArray */
  fields: FieldArrayWithId<FieldValues, "metadata.office_contact_pairs", "id">[];
  /** Append a new pair */
  onAppend: (value: OfficeContactPair) => void;
  /** Remove a pair by index */
  onRemove: (index: number) => void;
  /** Update a pair by index */
  onUpdate: (index: number, value: OfficeContactPair) => void;
  /** Form errors */
  errors?: FieldErrors<FieldValues>;
  // Standalone mode props should not be present
  initialPairs?: never;
  onChange?: never;
};

// Standalone mode props (for OnboardingTab, etc.)
type StandaloneModeProps = {
  /** Initial office contact pairs */
  initialPairs?: OfficeContactPair[];
  /** Callback when pairs change */
  onChange: (pairs: OfficeContactPair[]) => void;
  // Form mode props should not be present
  fields?: never;
  onAppend?: never;
  onRemove?: never;
  onUpdate?: never;
  errors?: never;
};

type EliteMetadataConfigurationProps = {
  /** The company/firm ID to fetch offices and contacts for */
  firmId: number | string | null;
  /** Pre-fetched office options (optional - if provided, won't fetch) */
  officeOptions?: Entity[];
  /** Whether the selection is optional (0) or required (>0) */
  isOptional?: boolean;
  /** Whether the component is disabled */
  disabled?: boolean;
} & (FormModeProps | StandaloneModeProps);

export function EliteMetadataConfiguration(props: EliteMetadataConfigurationProps) {
  const {
    firmId,
    officeOptions: providedOfficeOptions,
    isOptional = false,
    disabled = false,
  } = props;

  // Determine which mode we're in
  const isFormMode = "fields" in props && props.fields !== undefined;

  // For standalone mode, we need local state
  const [standalonePairs, setStandalonePairs] = useState<OfficeContactPair[]>(
    !isFormMode ? props.initialPairs || [] : [],
  );
  const [fetchedOfficeOptions, setFetchedOfficeOptions] = useState<Entity[]>([]);
  const [contactsByOffice, setContactsByOffice] = useState<Record<number, Entity[]>>({});
  const [loadingContactsForOffice, setLoadingContactsForOffice] = useState<Set<number>>(new Set());
  const [isLoadingOffices, setIsLoadingOffices] = useState(false);

  // Use provided office options or fetched ones
  const officeOptions = providedOfficeOptions || fetchedOfficeOptions;

  // Fetch offices when firmId is available (only if not provided)
  useEffect(() => {
    // Skip fetching if office options are provided
    if (providedOfficeOptions) {
      return;
    }

    if (!firmId) {
      setFetchedOfficeOptions([]);
      return;
    }

    const companyId = Number(firmId);
    if (Number.isNaN(companyId)) return;

    const fetchOffices = async () => {
      setIsLoadingOffices(true);
      try {
        const response = await officesService.getAll({ companyWpId: companyId });
        const options = response.offices.map(office => {
          const name = office.name || `Office ${office.officeId}`;
          const location = [office.city, office.countryName].filter(Boolean).join(", ");
          return {
            value: office.officeId,
            label: location ? `${name} (${location})` : name,
            description: `ID: ${office.officeId}`,
          };
        });
        setFetchedOfficeOptions(options);
      } catch (error) {
        console.error("Failed to fetch offices:", error);
        setFetchedOfficeOptions([]);
      } finally {
        setIsLoadingOffices(false);
      }
    };

    fetchOffices();
  }, [firmId, providedOfficeOptions]);

  // Get the pairs array depending on mode
  const getPairs = useCallback((): OfficeContactPair[] => {
    if (isFormMode) {
      return (props.fields as unknown as OfficeContactPair[]) || [];
    }
    return standalonePairs;
  }, [isFormMode, props, standalonePairs]);

  // Load contacts for existing office selections
  useEffect(() => {
    const loadContactsForExistingPairs = async () => {
      const currentPairs = getPairs();
      for (const pair of currentPairs) {
        if (pair.office_id && !contactsByOffice[pair.office_id]) {
          await fetchContactsForOffice(pair.office_id);
        }
      }
    };
    loadContactsForExistingPairs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFormMode ? (props as FormModeProps).fields?.length : standalonePairs.length]);

  // Fetch contacts for a specific office
  const fetchContactsForOffice = useCallback(
    async (officeId: number) => {
      if (contactsByOffice[officeId] || loadingContactsForOffice.has(officeId)) {
        return; // Already loaded or loading
      }

      setLoadingContactsForOffice(prev => new Set(prev).add(officeId));

      try {
        const response = await contactsService.getAll({ officeWpId: officeId });
        const options = response.contacts.map(contact => ({
          value: contact.contactId,
          label: contact.name || `Contact ${contact.contactId}`,
          description: contact.email ?? undefined,
        }));
        setContactsByOffice(prev => ({ ...prev, [officeId]: options }));
      } catch (error) {
        console.error("Failed to fetch contacts for office:", officeId, error);
        setContactsByOffice(prev => ({ ...prev, [officeId]: [] }));
      } finally {
        setLoadingContactsForOffice(prev => {
          const next = new Set(prev);
          next.delete(officeId);
          return next;
        });
      }
    },
    [contactsByOffice, loadingContactsForOffice],
  );

  // Handlers that work with both modes
  const handleAddOffice = () => {
    if (isFormMode) {
      (props as FormModeProps).onAppend({ office_id: 0, contact_ids: [] });
    } else {
      const newPairs = [...standalonePairs, { office_id: 0, contact_ids: [] }];
      setStandalonePairs(newPairs);
      (props as StandaloneModeProps).onChange(newPairs);
    }
  };

  const handleRemoveOffice = (index: number) => {
    if (isFormMode) {
      (props as FormModeProps).onRemove(index);
    } else {
      const newPairs = standalonePairs.filter((_, i) => i !== index);
      setStandalonePairs(newPairs);
      (props as StandaloneModeProps).onChange(newPairs);
    }
  };

  const handleOfficeChange = async (index: number, officeId: number) => {
    if (isFormMode) {
      (props as FormModeProps).onUpdate(index, {
        office_id: officeId,
        contact_ids: [],
      });
    } else {
      const newPairs = standalonePairs.map((pair, i) =>
        i === index ? { ...pair, office_id: officeId, contact_ids: [] } : pair,
      );
      setStandalonePairs(newPairs);
      (props as StandaloneModeProps).onChange(newPairs);
    }
    if (officeId && !contactsByOffice[officeId]) {
      await fetchContactsForOffice(officeId);
    }
  };

  const handleAddContact = (index: number, contactId: number) => {
    const currentPairs = getPairs();
    const currentPair = currentPairs[index];
    const newContactIds = [...(currentPair.contact_ids || []), contactId];

    if (isFormMode) {
      (props as FormModeProps).onUpdate(index, {
        office_id: currentPair.office_id,
        contact_ids: newContactIds,
      });
    } else {
      const newPairs = standalonePairs.map((pair, i) =>
        i === index ? { ...pair, contact_ids: newContactIds } : pair,
      );
      setStandalonePairs(newPairs);
      (props as StandaloneModeProps).onChange(newPairs);
    }
  };

  const handleRemoveContact = (index: number, contactId: number) => {
    const currentPairs = getPairs();
    const currentPair = currentPairs[index];
    const newContactIds = (currentPair.contact_ids || []).filter(id => id !== contactId);

    if (isFormMode) {
      (props as FormModeProps).onUpdate(index, {
        office_id: currentPair.office_id,
        contact_ids: newContactIds,
      });
    } else {
      const newPairs = standalonePairs.map((pair, i) =>
        i === index ? { ...pair, contact_ids: newContactIds } : pair,
      );
      setStandalonePairs(newPairs);
      (props as StandaloneModeProps).onChange(newPairs);
    }
  };

  // Get pairs with IDs for rendering (form mode has IDs, standalone uses index)
  const pairs: (OfficeContactPair & { id: string })[] = isFormMode
    ? ((props as FormModeProps).fields as unknown as (OfficeContactPair & {
        id: string;
      })[])
    : standalonePairs.map((pair, index) => ({
        ...pair,
        id: `standalone-${index}`,
      }));

  const selectedOfficeIds = pairs.map(p => p.office_id).filter(id => id > 0);

  // Get error messages from nested errors (form mode only)
  const getErrorForIndex = (index: number): string | undefined => {
    if (!isFormMode) return undefined;
    const errors = (props as FormModeProps).errors;
    const pairsErrors = (errors?.metadata as Record<string, unknown>)?.office_contact_pairs;
    if (Array.isArray(pairsErrors) && pairsErrors[index]) {
      const itemError = pairsErrors[index] as {
        contact_ids?: { message?: string };
      };
      return itemError?.contact_ids?.message;
    }
    return undefined;
  };

  // Get array-level error message
  const arrayErrorMessage = isFormMode
    ? (
        (props as FormModeProps).errors?.metadata as FieldErrors<{
          office_contact_pairs: OfficeContactPair[];
        }>
      )?.office_contact_pairs?.message ||
      (
        (props as FormModeProps).errors?.metadata as FieldErrors<{
          office_contact_pairs: OfficeContactPair[];
        }>
      )?.office_contact_pairs?.root?.message
    : undefined;

  if (!firmId) {
    return (
      <section className={styles.section}>
        <h3 className={styles.sectionHeading}>
          Elite Lawyer Selection
          {isOptional ? (
            <span className={styles.optionalBadge}>(Optional)</span>
          ) : (
            <span className={styles.required}>*</span>
          )}
        </h3>
        <div className={styles.infoState}>Select a company first to configure Elite lawyers.</div>
      </section>
    );
  }

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionHeading}>
        Elite Lawyer Selection
        {isOptional ? (
          <span className={styles.optionalBadge}>(Optional)</span>
        ) : (
          <span className={styles.required}>*</span>
        )}
      </h3>
      <p className={styles.helperText}>
        Select offices and the lawyers who will have enhanced Elite profiles at each office
        location. A lawyer can have Elite visibility at one office but not another.
      </p>

      {isLoadingOffices ? (
        <div className={styles.loadingState}>Loading offices...</div>
      ) : officeOptions.length === 0 ? (
        <div className={styles.errorState}>No offices found for this company.</div>
      ) : (
        <div className={styles.officeContactsContainer}>
          {pairs.map((pair, index) => {
            const availableOffices = officeOptions.filter(
              o => !selectedOfficeIds.includes(o.value as number) || o.value === pair.office_id,
            );
            const officeContacts = pair.office_id ? contactsByOffice[pair.office_id] || [] : [];
            const isLoadingContacts = pair.office_id
              ? loadingContactsForOffice.has(pair.office_id)
              : false;
            const contactIds = pair.contact_ids || [];
            const availableContacts = officeContacts.filter(
              c => !contactIds.includes(c.value as number),
            );
            const itemError = getErrorForIndex(index);

            return (
              <div
                key={pair.id}
                className={`${styles.officeRow} ${itemError ? styles.officeRowError : ""}`}
              >
                <div className={styles.officeRowHeader}>
                  <span className={styles.officeRowTitle}>Office {index + 1}</span>
                  {pairs.length > 1 && (
                    <Button
                      variant="text"
                      color="error"
                      size="small"
                      onClick={() => handleRemoveOffice(index)}
                      disabled={disabled}
                    >
                      <Icon icon="cross" size="small" />
                      Remove
                    </Button>
                  )}
                </div>

                <div className={styles.officeRowContent}>
                  <div className={styles.officeSelectors}>
                    <SearchableSelect
                      name={`office-${index}`}
                      label="Office"
                      required={!isOptional}
                      value={pair.office_id || null}
                      options={availableOffices.map(o => ({
                        value: o.value as number,
                        label: String(o.label),
                        description: o.description,
                      }))}
                      placeholder="Select an office..."
                      onChange={value => handleOfficeChange(index, value as number)}
                      disabled={disabled}
                    />

                    <SearchableSelect
                      name={`contact-${index}`}
                      label="Add Lawyer"
                      value={null}
                      options={availableContacts.map(c => ({
                        value: c.value as number,
                        label: String(c.label),
                        description: c.description,
                      }))}
                      placeholder={
                        isLoadingContacts
                          ? "Loading..."
                          : !pair.office_id
                            ? "Select office first"
                            : "Search lawyers..."
                      }
                      disabled={disabled || !pair.office_id || isLoadingContacts}
                      onChange={value => {
                        if (value) handleAddContact(index, value as number);
                      }}
                    />
                  </div>

                  <div className={styles.selectedContacts}>
                    <div className={styles.selectedContactsHeader}>
                      <span>Selected Lawyers</span>
                      <span className={styles.selectedCount}>{contactIds.length}</span>
                    </div>
                    <div className={styles.selectedContactsChips}>
                      {contactIds.length === 0 ? (
                        <span className={styles.emptyState}>No lawyers selected</span>
                      ) : (
                        contactIds.map(contactId => {
                          const contact = officeContacts.find(c => c.value === contactId);
                          return (
                            <Chip
                              key={contactId}
                              size="small"
                              variant="outlined"
                              onDelete={
                                disabled ? undefined : () => handleRemoveContact(index, contactId)
                              }
                            >
                              {contactId} - {contact?.label || `Contact ${contactId}`}
                            </Chip>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>

                {itemError && (
                  <div className={styles.itemError}>
                    <Icon icon="exclamation-circle" size="small" />
                    <span>{itemError}</span>
                  </div>
                )}
              </div>
            );
          })}

          <Button variant="outlined" color="primary" onClick={handleAddOffice} disabled={disabled}>
            <Icon icon="plus" size="small" />
            Add Office
          </Button>
        </div>
      )}

      {arrayErrorMessage && (
        <div className={styles.errorBanner}>
          <Icon icon="exclamation-circle" size="small" className={styles.errorIcon} />
          <span className={styles.errorText}>{String(arrayErrorMessage)}</span>
        </div>
      )}
    </section>
  );
}
